// Defensive coercion helpers for zustand `persist` rehydration.
//
// A persisted localStorage value can be stale (written by an older bundle),
// truncated (quota write cut short), or hand-corrupted. When the rehydrated
// shape doesn't match what a component expects — most dangerously an array
// field arriving as `undefined`/an object — the first `.map`/`.find`/`.filter`
// throws during render. Because the SPA mounts its panes under one app-root
// error boundary, that single throw blanks the WHOLE app (see AppLayout). These
// helpers let each store's `migrate`/`merge` coerce persisted values back to
// the current shape so a `.map` target is always an array and rehydration can
// never throw. Pure + unit-tested so the guarantee is a permanent record.

export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function asRecord(
  value: unknown,
  fallback: Record<string, unknown> = {},
): Record<string, unknown> {
  return isPlainObject(value) ? value : fallback
}

// Guarantees an array target for a `.map`/`.filter`/`.find`. A non-array
// persisted value (undefined, object, null) yields the fallback, never a throw.
export function asArray<T>(value: unknown, fallback: T[] = []): T[] {
  return Array.isArray(value) ? (value as T[]) : fallback
}

export function asStringArray(
  value: unknown,
  fallback: string[] = [],
): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : fallback
}

export function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

export function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback
}

export function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

export function clampNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  return Math.min(max, Math.max(min, asFiniteNumber(value, fallback)))
}
